import { toast } from 'react-toastify';

/**
 * SW2-2458. Finding photos that upload after the finding has been saved, so the
 * form can close straight away instead of waiting on a slow connection.
 *
 * A plain module (not React state) so the API layer can queue work and any
 * component can watch it. Jobs run one at a time, in the order queued: a
 * closure-photo save has to finish before anything else touches that finding,
 * and one upload at a time is also kinder to a weak cellular link.
 *
 * Jobs live in memory only (decided on SW2-2458): closing the tab loses what is
 * still pending, so `BackgroundUploadsProvider` asks before the page unloads.
 * The finding itself is always saved before its job is queued.
 */

export type BackgroundJobStatus = 'queued' | 'uploading' | 'failed' | 'done';

export type UploadProgress = (fraction: number) => void;

export interface BackgroundJobInput {
  /** Finding photos block signing their inspection; site photos do not. */
  kind: 'finding' | 'sitePhotos';
  inspectionId?: number;
  findingId?: number;
  /** Every finding a job touches (a bulk completion); defaults to [findingId]. */
  findingIds?: number[];
  projectId?: number;
  folderId?: number;
  /** Shown to the user, e.g. "4 photos". */
  photoCount: number;
  /** Does the work; may be called again by retry(), so it must be re-runnable. */
  run: (onProgress: UploadProgress) => Promise<unknown>;
}

export interface BackgroundJob extends BackgroundJobInput {
  id: number;
  status: BackgroundJobStatus;
  /** 0–1 for the request in flight. */
  progress: number;
  error?: unknown;
}

export type JobFilter = {
  kind?: BackgroundJobInput['kind'];
  inspectionId?: number;
  findingId?: number;
  folderId?: number;
};

/** The findings a job touches. */
export const jobFindingIds = (job: BackgroundJobInput): number[] =>
  (job.findingIds && job.findingIds.length
    ? job.findingIds
    : job.findingId !== undefined
    ? [job.findingId]
    : []
  ).map(Number);

/** Every key given must match; ids compare as numbers. */
export function matches(job: BackgroundJob, filter: JobFilter): boolean {
  return (Object.keys(filter) as (keyof JobFilter)[]).every((key) => {
    const want = filter[key];

    if (want === undefined) return true;
    if (key === 'kind') return job.kind === want;
    if (key === 'findingId') return jobFindingIds(job).includes(Number(want));

    return Number(job[key]) === Number(want);
  });
}

type Listener = (jobs: readonly BackgroundJob[]) => void;
type DoneListener = (job: BackgroundJob) => void;

let nextId = 1;
let jobs: BackgroundJob[] = [];
let running = false;
const listeners = new Set<Listener>();
const doneListeners = new Set<DoneListener>();

function emit() {
  const snapshot = [...jobs];

  listeners.forEach((listener) => listener(snapshot));
}

function patch(id: number, changes: Partial<BackgroundJob>) {
  jobs = jobs.map((job) => (job.id === id ? { ...job, ...changes } : job));
  emit();
}

async function runQueue() {
  if (running) return;
  running = true;

  try {
    for (;;) {
      const job = jobs.find((j) => j.status === 'queued');

      if (!job) break;

      patch(job.id, { status: 'uploading', progress: 0, error: undefined });

      try {
        await job.run((fraction) =>
          patch(job.id, { progress: Math.max(0, Math.min(1, fraction)) }),
        );
        patch(job.id, { status: 'done', progress: 1 });

        const done = jobs.find((j) => j.id === job.id);

        if (done) doneListeners.forEach((listener) => listener(done));
      } catch (error) {
        patch(job.id, { status: 'failed', error });
      }
    }
  } finally {
    running = false;
  }
}

export const backgroundPhotoUploads = {
  enqueue(input: BackgroundJobInput): number {
    const job: BackgroundJob = {
      ...input,
      id: nextId++,
      status: 'queued',
      progress: 0,
    };

    jobs = [...jobs, job];
    emit();
    runQueue();

    return job.id;
  },

  retry(id: number) {
    const job = jobs.find((j) => j.id === id);

    if (!job || job.status !== 'failed') return;

    patch(id, { status: 'queued', progress: 0, error: undefined });
    runQueue();
  },

  /** Drop a finished or failed job (the user dismissed its notice). */
  dismiss(id: number) {
    jobs = jobs.filter(
      (job) => job.id !== id || job.status === 'queued' || job.status === 'uploading',
    );
    emit();
  },

  getJobs(): readonly BackgroundJob[] {
    return jobs;
  },

  /** Queued, uploading or failed: photos that are not on the server yet. */
  pending(filter: JobFilter = {}) {
    return jobs.filter((job) => job.status !== 'done' && matches(job, filter));
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  },

  /** Called once per job when it succeeds, e.g. to reload the inspection. */
  onJobDone(listener: DoneListener): () => void {
    doneListeners.add(listener);

    return () => {
      doneListeners.delete(listener);
    };
  },

  /** Tests only. */
  reset() {
    jobs = [];
    running = false;
    nextId = 1;
    listeners.clear();
    doneListeners.clear();
  },
};

/**
 * Warn and return true when photos are still uploading, so an action that
 * would reload the page (switching tenant, signing out) can refuse.
 */
export function pendingUploadsWarning(action: string): boolean {
  const photos = backgroundPhotoUploads
    .pending()
    .reduce((sum, job) => sum + job.photoCount, 0);

  if (photos === 0) return false;

  toast.warning(
    `Wait for ${photos} photo${photos === 1 ? '' : 's'} to finish uploading before ${action}.`,
  );

  return true;
}

/**
 * A background job must reach the server. Offline, a finding job's
 * withOfflineWrite would take its offline branch before even trying (queueing
 * nothing for an inspection that was not downloaded), and a site-photo job's
 * upload would fail like a request cut off mid-way. Fail first instead, marked
 * not-sent, so the notice offers Retry, retries by itself once back online,
 * and does not warn that the photos may already be on the server.
 */
export function assertOnline() {
  if (!navigator.onLine) {
    const err: any = new Error('No connection.');

    err.request = {};
    // Nothing reached the server, so retrying can never duplicate anything:
    // BackgroundUploadsNotifier retries these by itself once back online.
    err.notSent = true;
    throw err;
  }
}
